import { useLocation } from 'react-router-dom';
import { Link } from '~/utils/navigation';
import styled, { css } from 'styled-components';
import theme from 'styled-theming';
import queryString from 'query-string';
import { LabelBold } from '@comeon/ui';

import SkeletonPlaceholder from './utilities/pulsating-placeholder';
import breakpoint from '~/utils/style-utils';
import type { ReactNode } from 'react';

export function useFilters() {
	// Gets the filter parameter from the URL. Since we have URLs for both main content and sidebar content, we need to check for both of of those.
	// If the paramter is in the "sidebar-url" we use querystring method to parse the url
	const { search } = window.location;
	const parsedQuery = queryString.parse(search);

	const mainFilter = new URLSearchParams(search).get('filter');

	const sidebarFilter =
		typeof parsedQuery.sidebar === 'string' ? new URLSearchParams(parsedQuery.sidebar).get('filter') : null;

	return mainFilter || sidebarFilter;
}

function useQuery() {
	return new URLSearchParams(useLocation().search);
}

const pillBrandException = theme('brand', {
	lyllo: css`
		h5 {
			text-transform: uppercase;
		}
	`
});

export const PillStyles = styled(Link)<{ $active?: boolean }>`
	background-color: ${({ $active }) => ($active ? `var(--filter-pills-bg-active)` : `var(--filter-pills-bg)`)};
	border: ${({ $active }) => ($active ? `var(--filter-pills-bg-active-border)` : `0`)};
	border-radius: var(--brand-filter-pills-border-radius, 25px);
	display: inline-flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	margin-right: 8px;
	padding: 0 16px;
	height: 28px;
	text-align: center;
	transition: 200ms background-color ease-in;
	a,
	h5 {
		color: ${({ $active }) =>
			$active ? `var(--filter-pills-text-color-active)` : `var(--filter-pills-text-color)`};
		font-size: 12px;
		line-height: 16px;
	}
	${breakpoint.lg`
		height: 36px;
		padding: 0px 12px;

		h5 {
			font-size: 15px;
			line-height: 20px;
		}
	`};
	${pillBrandException};
`;

export const PillsContainerStyles = styled.div<{
	overflowVisible?: boolean;
	disablePadding?: boolean;
}>`
	display: flex;
	flex-direction: row;
	background-color: var(--filter-bar-bg, transparent);
	overflow: ${({ overflowVisible }) => (overflowVisible ? 'visible' : 'auto')};
	white-space: nowrap;
	padding: ${({ disablePadding }) => (disablePadding ? 0 : '12px 12px 0 12px')};
	max-width: 100vw;
	${({ disablePadding }) => breakpoint.lg`
 		padding: ${disablePadding ? 0 : '12px'};
	`}
	white-space: nowrap;
`;

type PillProps = {
	children: ReactNode;
	active: boolean;
	to: string;
	dataAt: string;
};

export const Pill = ({ children, active, to, dataAt }: PillProps) => (
	<PillStyles $active={active} href={to} data-at={dataAt}>
		<LabelBold>{children}</LabelBold>
	</PillStyles>
);

type PillsContainerProps = {
	pills?: {
		href: string;
		label: string;
	}[];
};
const PillsContainer = ({ pills = [{ label: 'All', href: 'all' }] }: PillsContainerProps) => {
	const { pathname: url } = useLocation();
	const query = useQuery();
	const activePill = useFilters();

	// If the clicked pill is the same as the currently selected one,
	// remove the filter - otherwise, select the clicked pill.
	const formatURL = (filter: string, location: string) => {
		query.delete('filter');
		if (activePill !== filter) {
			query.set('filter', filter);
		}
		const queryAsString = query.toString();

		return `${location}${queryAsString ? `?${queryAsString}` : ''}`;
	};

	return (
		<PillsContainerStyles data-testid="pills-container">
			{pills.map((pill, index) => (
				<Pill
					key={pill.href}
					active={activePill === pill.href}
					to={formatURL(pill.href, url)}
					dataAt={`filter-pill-${index}`}
				>
					{pill.label}
				</Pill>
			))}
		</PillsContainerStyles>
	);
};

export const PillsSkeletonPlaceholder = ({ numberOfPlaceholders }: { numberOfPlaceholders: number }) => {
	const PLACE_HOLDER_STYLES = { marginBottom: 0, height: 14, width: 80 };
	const PLACE_HOLDERS = Array(numberOfPlaceholders)
		.fill(null)
		.map((_, i) => i)
		.map(key => (
			<PillStyles key={key} href="">
				<SkeletonPlaceholder style={PLACE_HOLDER_STYLES} />
			</PillStyles>
		));
	return <PillsContainerStyles>{PLACE_HOLDERS}</PillsContainerStyles>;
};

export default PillsContainer;
